import { useState } from 'react';
import { useApp } from '@context/AppContext';
import {
  Undo2, Redo2, Download, Users, Crown, Palette, Copy, Check, Image, Play, HelpCircle, LogOut, Trash2,
  RotateCcw, RotateCw, Maximize2, Minimize2, FlipHorizontal, Paintbrush, Eraser as EraseIcon, ArrowRightLeft,
  BookOpen, Expand,
} from 'lucide-react';
import SignInButton from '@components/SignInButton';

export default function TopBar({
  onUndo, onRedo, onExport, onToggleGallery, onToggleReplay, onToggleHelp, onToggleArtStyles,
  onLeaveRoom, onSwitchRoom, onDeleteSelected, onTransformSelected, onRecolourSelected, onClearMyStrokes,
  canUndo, canRedo, hasSelection, galleryOpen, replayOpen, helpOpen, artStylesOpen, recentRooms,
}) {
  const { state, dispatch } = useApp();
  const { room, user, ui, canvas } = state;
  const [copied, setCopied] = useState(false);
  const [switchOpen, setSwitchOpen] = useState(false);
  const [switchCode, setSwitchCode] = useState('');
  const [switchError, setSwitchError] = useState('');

  const handleCopyCode = async () => {
    const text = `Join me on DrawLab! Room code: ${room.code} — https://ddd-draw-lab.web.app/app/room/${room.code}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Ignore
    }
  };

  const btnClass = (active) =>
    `w-9 h-9 flex items-center justify-center rounded-lg transition-colors ${
      active ? 'text-amber bg-amber/10' : 'text-slate-400 hover:text-charcoal hover:bg-slate-100'
    }`;

  return (
    <div className="h-12 bg-white border-b border-slate-100 flex items-center px-3 gap-1 shrink-0 overflow-x-auto">
      {/* Logo */}
      <div className="flex items-center gap-1.5 mr-2 shrink-0">
        <Palette className="w-5 h-5 text-amber" />
        <span className="text-sm font-bold font-display text-charcoal hidden sm:block">DrawLab</span>
      </div>

      {/* Room name + code */}
      <div className="flex items-center gap-2 min-w-0 shrink-0 relative">
        <span className="text-sm text-charcoal font-display truncate hidden md:block">{room.name}</span>
        <button onClick={handleCopyCode}
          className="flex items-center gap-1 px-2 py-1 bg-amber/10 text-amber rounded-lg text-xs font-mono font-medium hover:bg-amber/20 transition-colors shrink-0"
          title="Copy room invite link">
          {room.code}
          {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
        </button>
        <button
          onClick={() => { setSwitchOpen(!switchOpen); setSwitchCode(''); setSwitchError(''); }}
          title="Switch to another room"
          className={`w-7 h-7 flex items-center justify-center rounded-lg transition-colors shrink-0 ${
            switchOpen ? 'text-amber bg-amber/10' : 'text-slate-300 hover:text-amber hover:bg-amber/10'
          }`}
        >
          <ArrowRightLeft className="w-3.5 h-3.5" />
        </button>

        {/* Switch room popover + backdrop */}
        {switchOpen && (
          <>
          <div className="fixed inset-0 z-40 bg-black/10" onClick={() => setSwitchOpen(false)} />
          <div className="fixed top-14 left-1/2 -translate-x-1/2 sm:absolute sm:top-full sm:left-0 sm:translate-x-0 mt-1 z-50 bg-white rounded-xl shadow-xl border border-slate-200 p-3 w-72 sm:w-64">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-display text-slate-400">Switch to another room</p>
              <button onClick={() => setSwitchOpen(false)} className="w-5 h-5 flex items-center justify-center rounded text-slate-300 hover:text-charcoal transition-colors">
                <span className="text-xs">✕</span>
              </button>
            </div>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const code = switchCode.toUpperCase().replace(/[^A-Z0-9]/g, '');
              if (code.length < 6) { setSwitchError('Code must be at least 6 characters'); return; }
              if (code === room.code) { setSwitchError("You're already in this room"); return; }
              setSwitchError('');
              const result = await onSwitchRoom?.(code);
              if (result?.error) { setSwitchError(result.error); }
              else { setSwitchOpen(false); }
            }} className="flex gap-2">
              <input
                type="text"
                value={switchCode}
                onChange={(e) => setSwitchCode(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8))}
                placeholder="ROOM CODE"
                maxLength={8}
                autoFocus
                className="flex-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-canvas text-charcoal text-sm font-mono tracking-wider uppercase placeholder:text-slate-300 focus:outline-none focus:border-amber/50"
              />
              <button
                type="submit"
                className="px-3 py-1.5 bg-amber text-white text-xs font-display font-semibold rounded-lg hover:bg-amber/90 transition-colors"
              >
                Join
              </button>
            </form>
            {switchError && (
              <p className="text-xs text-red-500 font-display mt-1.5">{switchError}</p>
            )}
            {/* Recent rooms for quick jump */}
            {recentRooms && recentRooms.length > 0 && (
              <div className="mt-2 pt-2 border-t border-slate-100">
                <p className="text-[10px] font-display text-slate-300 uppercase tracking-wide mb-1">Recent</p>
                <div className="space-y-0.5 max-h-28 overflow-y-auto">
                  {recentRooms.filter(r => r.code !== room.code).slice(0, 5).map((r) => (
                    <button
                      key={r.code}
                      onClick={async () => {
                        setSwitchError('');
                        const result = await onSwitchRoom?.(r.code);
                        if (result?.error) { setSwitchError(result.error); }
                        else { setSwitchOpen(false); }
                      }}
                      className="w-full flex items-center justify-between px-2 py-1.5 rounded-lg hover:bg-slate-50 transition-colors text-left"
                    >
                      <span className="text-xs text-charcoal font-display truncate">{r.name || 'Unnamed'}</span>
                      <span className="text-[10px] font-mono text-amber bg-amber/10 px-1.5 py-0.5 rounded shrink-0 ml-2">{r.code}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          </>
        )}
      </div>

      <div className="flex-1" />

      {/* Action buttons */}
      <div className="flex items-center gap-0.5 shrink-0">
        {/* Undo / Redo */}
        <button onClick={onUndo} disabled={!canUndo} title="Undo (⌘Z)"
          className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-charcoal hover:bg-slate-100 transition-colors disabled:opacity-30">
          <Undo2 className="w-4 h-4" />
        </button>
        <button onClick={onRedo} disabled={!canRedo} title="Redo (⌘⇧Z)"
          className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-charcoal hover:bg-slate-100 transition-colors disabled:opacity-30">
          <Redo2 className="w-4 h-4" />
        </button>

        {/* Selection controls */}
        {hasSelection && (
          <div className="flex items-center gap-0.5 px-1 border-l border-r border-slate-200 mx-1">
            <button onClick={() => onTransformSelected('rotate', -15)} title="Rotate left 15°"
              className="w-7 h-7 flex items-center justify-center rounded text-slate-400 hover:text-charcoal hover:bg-slate-100 transition-colors">
              <RotateCcw className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onTransformSelected('rotate', 15)} title="Rotate right 15°"
              className="w-7 h-7 flex items-center justify-center rounded text-slate-400 hover:text-charcoal hover:bg-slate-100 transition-colors">
              <RotateCw className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onTransformSelected('scale', 1.2)} title="Scale up"
              className="w-7 h-7 flex items-center justify-center rounded text-slate-400 hover:text-charcoal hover:bg-slate-100 transition-colors">
              <Maximize2 className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onTransformSelected('scale', 0.8)} title="Scale down"
              className="w-7 h-7 flex items-center justify-center rounded text-slate-400 hover:text-charcoal hover:bg-slate-100 transition-colors">
              <Minimize2 className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onTransformSelected('flip', null)} title="Flip horizontal"
              className="w-7 h-7 flex items-center justify-center rounded text-slate-400 hover:text-charcoal hover:bg-slate-100 transition-colors">
              <FlipHorizontal className="w-3.5 h-3.5" />
            </button>
            {/* Recolour — apply current palette colour to selection */}
            <button onClick={() => onRecolourSelected(canvas.colour)} title={`Recolour to ${canvas.colour}`}
              className="w-7 h-7 flex items-center justify-center rounded hover:bg-slate-100 transition-colors">
              <div className="w-4 h-4 rounded-full border border-slate-300" style={{ backgroundColor: canvas.colour }} />
            </button>
            <button onClick={onDeleteSelected} title="Delete (Del)"
              className="w-7 h-7 flex items-center justify-center rounded text-red-400 hover:text-red-600 hover:bg-red-50 transition-colors">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        {/* Clear my strokes */}
        <button onClick={onClearMyStrokes} title="Clear my strokes"
          className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-charcoal hover:bg-slate-100 transition-colors">
          <EraseIcon className="w-4 h-4" />
        </button>

        <button onClick={onExport} title="Export as PNG" className={btnClass(false)}>
          <Download className="w-4 h-4" />
        </button>
        <button onClick={onToggleReplay} title="Replay session" className={btnClass(replayOpen)}>
          <Play className="w-4 h-4" />
        </button>
        <button onClick={onToggleGallery} title="Gallery" className={btnClass(galleryOpen)}>
          <Image className="w-4 h-4" />
        </button>
        <button onClick={() => dispatch({ type: 'TOGGLE_MEMBERS' })} title="Members" className={btnClass(ui.membersOpen)}>
          <Users className="w-4 h-4" />
        </button>
        {user.isHost && (
          <button onClick={() => dispatch({ type: 'TOGGLE_HOST_PANEL' })} title="Host controls"
            className={`w-8 h-8 flex items-center justify-center rounded-lg transition-colors ${
              ui.hostPanelOpen ? 'text-amber bg-amber/10' : 'text-amber/60 hover:text-amber hover:bg-amber/10'
            }`}>
            <Crown className="w-4 h-4" />
          </button>
        )}
        <button onClick={onToggleArtStyles} title="Art Styles — the workshop curriculum" className={btnClass(artStylesOpen)}>
          <BookOpen className="w-4 h-4" />
        </button>
        <button
          onClick={() => dispatch({ type: 'TOGGLE_FOCUS_MODE' })}
          title="Focus mode — hide controls for more canvas"
          className={btnClass(false)}
        >
          <Expand className="w-4 h-4" />
        </button>
        <button onClick={onToggleHelp} title="Help" className={btnClass(helpOpen)}>
          <HelpCircle className="w-4 h-4" />
        </button>
        <SignInButton />
        <button onClick={onLeaveRoom} title="Leave room"
          className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors">
          <LogOut className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
